import React, {lazy, Suspense} from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import {createTheme, CssBaseline, ThemeProvider} from "@mui/material";
import {Provider} from "react-redux";
import {appStore, storePersistor} from "./store/AppStore";
import {createBrowserRouter, RouterProvider} from "react-router-dom";
import {AppRoute} from "./book/AppRoute";
import i18n from "./book/i18n";
import {I18nextProvider} from "react-i18next";
import {PersistGate} from "redux-persist/integration/react";
import {Loader} from "./component/common/loader/Loader";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import {UsersManagement} from "./component/page/users-management/UsersManagement";
import {ProductList} from "./component/page/product-list/ProductList";
import {Credits} from "./component/page/credits/Credits";
import {OfflineActivation} from "./component/page/offline-activation/OfflineActivation";
import {ProductDetails} from "./component/page/product-details/ProductDetails";
import {UserActivityLog} from './component/page/user-activity-log/UserActivityLog';
import * as Sentry from "@sentry/react";

const theme = createTheme({
    palette: {
        primary: {
            main: '#FE0000'
        },
        secondary: {
            main: '#FE0000'
        },
    },
});

const Login = lazy(() => import('./component/page/login/Login').then(module => ({ default: module.Login })));
const OrderList = lazy(() => import('./component/page/order-list/OrderList').then(module => ({ default: module.OrderList })));
const OrderDetails = lazy(() => import('./component/page/order-details/OrderDetails').then(module => ({ default: module.OrderDetails })));
const ActivationList = lazy(() => import('./component/page/activation-list/ActivationList').then(module => ({ default: module.ActivationList })));
const TrialActivationList = lazy(() => import('./component/page/trial-activation-list/TrialActivationList').then(module => ({ default: module.TrialActivationList })));
const ActivationDetails = lazy(() => import('./component/page/activation-details/ActivationDetails').then(module => ({ default: module.ActivationDetails })));

const router = createBrowserRouter([
    {
        path: AppRoute.Login,
        Component: Login,
    },
    {
        path: AppRoute.Home,
        Component: OrderList,
    },
    {
        path: AppRoute.OrderList,
        Component: OrderList,
    },
    {
        path: AppRoute.Order,
        Component: OrderDetails,
    },
    {
        path: AppRoute.ActivationList,
        Component: ActivationList,
    },
    {
        path: AppRoute.TrialActivationList,
        Component: TrialActivationList,
    },
    {
        path: AppRoute.Activation,
        Component: ActivationDetails,
    },
    {
        path: AppRoute.UsersManagement,
        Component: UsersManagement,
    },
    {
        path: AppRoute.Products,
        Component: ProductList,
    },
    {
        path: AppRoute.Product,
        Component: ProductDetails,
    },
    {
        path: AppRoute.UserActivityLog,
        Component: UserActivityLog,
    },
    {
        path: AppRoute.Credits,
        Component: Credits,
    },
    {
        path: AppRoute.OffLineActivation,
        Component: OfflineActivation,
    },
]);

const sentryDsn: string = import.meta.env.VITE_SENTRY_DSN as string;
const sentryEnvironment: string = import.meta.env.VITE_ENVIRONMENT as string;

Sentry.init({
    dsn: sentryDsn,
    debug: false,
    environment: sentryEnvironment,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
        "localhost",
        /^https:\/\/climfrontenddev.z6.web.core.windows.net/,
        /^https:\/\/licensesmanager-qa.corp.carel.com/,
        /^https:\/\/licensesmanager.corp.carel.com/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
);
root.render(<React.StrictMode>
    <ThemeProvider theme={theme}>
        <Provider store={appStore}>
            <Suspense fallback={<Loader/>}>
                <I18nextProvider i18n={i18n}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <PersistGate loading={null} persistor={storePersistor}>
                            <CssBaseline/>
                            <RouterProvider router={router}/>
                        </PersistGate>
                    </LocalizationProvider>
                </I18nextProvider>
            </Suspense>
        </Provider>
    </ThemeProvider>
</React.StrictMode>);
